import { T } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import CuriConfused from '../../../assets/icons/CuriConfused';
import { QuestionsSvg } from '../../../assets/icons/QuestionsSvg';
import theme from '../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import BackendService from '../../../services/backendService';
import { configureStore } from '../../../utils/store';
import { Button } from '../../_atoms/Button';
import BaseModalV2 from '../BaseModalV2';

export const CheckoutCanceledModal = () => {
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { config } = configureStore();
  const [priceId, setPriceId] = useState<string | undefined>(undefined);
  useEffect(() => {
    // Check if the checkout query param is set to "canceled"
    if (router.query.checkout === 'canceled') {
      setIsVisible(true);
      const priceId = router.query.priceId as string;
      if (priceId) {
        setPriceId(priceId);
      }
    }
  }, [router.query]);

  const handleClose = () => {
    setIsVisible(false);
    // Remove the query parameter from the URL
    const { checkout, ...rest } = router.query;
    router.replace(
      {
        pathname: router.pathname,
        query: rest,
      },
      undefined,
      { shallow: true },
    );
  };

  const handleTryAgain = async () => {
    // Only allow checkout for Freemium users
    if (userAccessBadge?.license?.coarseLevel !== LicenseCoarseLevels.freemium) {
      console.warn('Checkout only available for Freemium users');
      handleClose();
      return;
    }

    try {
      setIsLoading(true);

      const { url } = await BackendService.instance.users.createIndividualCheckoutSession(
        {
          priceId: priceId || config.stripe.monthlyIndividualPremiumPriceId,
          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing?checkout=canceled&priceId=${priceId}`,
        },
      );

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setIsLoading(false);
    }
  };

  return (
    <BaseModalV2
      visible={isVisible}
      onCancel={handleClose}
      centered
      width="500px"
      maskClosable={true}
    >
      <ModalContent>
        <IconWrapper>
          <CuriConfused />
        </IconWrapper>
        <Title>
          <T
            keyName="checkout.canceled.title"
            defaultValue="Oops! Looks like something came up"
          />
        </Title>
        <Description>
          <T
            keyName="checkout.canceled.description"
            defaultValue="We noticed you didn't complete your purchase. No worries! Your Premium plan is still waiting for you whenever you're ready."
          />
        </Description>
        <ButtonsContainer>
          <Button
            type="primary"
            onClick={handleTryAgain}
            disabled={isLoading}
            loading={isLoading}
          >
            <T keyName="checkout.canceled.try_again" defaultValue="Try Again" />
          </Button>
          <Button type="ghost" onClick={handleClose} disabled={isLoading}>
            <T keyName="checkout.canceled.maybe_later" defaultValue="Maybe Later" />
          </Button>
        </ButtonsContainer>
      </ModalContent>
    </BaseModalV2>
  );
};

const ModalContent = styled.div`
  padding: 40px 30px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (max-width: 600px) {
    padding: 30px 20px 20px;
  }
`;

const IconWrapper = styled.div`
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
  animation: wave 0.5s ease-in-out;

  svg {
    width: 100%;
    height: 100%;
  }

  @keyframes wave {
    0% {
      transform: rotate(0deg) scale(0.8);
    }
    50% {
      transform: rotate(10deg) scale(1.1);
    }
    100% {
      transform: rotate(0deg) scale(1);
    }
  }

  @media (max-width: 600px) {
    width: 60px;
    height: 60px;
    margin-bottom: 15px;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0 0 15px 0;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: ${theme.colors.text.dark};
  margin: 0 0 30px 0;
  max-width: 400px;

  @media (max-width: 600px) {
    font-size: 14px;
    margin-bottom: 25px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 350px;

  button {
    flex: 1;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 10px;
  }
`;
